/* Book a session: mentor, length, day, time, confirm. */

/*
  *** TWO COLUMNS, STEPS LEFT, SUMMARY RIGHT ***
  The steps used to stack down the page: three weeks of calendar is taller
  than a screen, so choosing a day pushed the time grid below the fold with
  nothing saying to scroll (reported 2026-10-31). Showing one step at a time
  fixed that but left most of the screen grey.

  The summary rail fixes the second problem. It is present from the first
  frame, so the page is never half-empty, it carries what has been chosen so
  far without the student having to remember, and Confirm lives in it rather
  than appearing only at the end.
*/
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 20rem;
  gap: var(--space-5);
  align-items: start;
}

/* --------------------------------------------------------- step rail */

/*
  A thin row of pills, not the tall tab strip it replaced. The strip carried
  each answer under its label, which the summary rail now does better and in
  one place — so these say only where you are.
*/
.steps {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--border);
}

.stepBtn {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-muted);
  background: none;
  border: 0;
  border-radius: var(--radius-full);
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}

.stepBtn:hover:not(:disabled) {
  color: var(--text);
  background: var(--bg-hover);
}

/* Not hidden: the shape of the task should not change as you move through
   it, so a step you cannot open yet is present and plainly inert. */
.stepBtn:disabled {
  color: var(--text-subtle);
  cursor: default;
}

.stepOn {
  color: var(--indigo);
  background: var(--indigo-soft);
}

.stepNum {
  display: grid;
  place-items: center;
  width: 1.375rem;
  height: 1.375rem;
  font-size: var(--text-xs);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
  background: var(--bg-sunken);
  border-radius: var(--radius-full);
}

.stepOn .stepNum {
  color: #fff;
  background: var(--indigo);
}

/* Answered, and not the step you are standing on. */
.stepDone .stepNum {
  color: #fff;
  background: var(--success);
}

.stepSep {
  flex-shrink: 0;
  width: 1rem;
  height: 1px;
  background: var(--border-strong);
}

/* The question the open step is asking. */
.panelTitle {
  margin: 0 0 var(--space-4);
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text);
}

.panelNote {
  margin: calc(var(--space-4) * -1) 0 var(--space-4);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/* ------------------------------------------------------------- mentors */

.mentorGrid {
  display: grid;
  gap: var(--space-3);
  /*
    A capped track, so ONE mentor renders as a card rather than a banner
    stretched across a 1600px screen — the same lesson as the library lobby.
  */
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
}

/*
  A button, not a div with a click handler: keyboard focus, Enter and Space
  all come free, and `aria-pressed` says which is chosen.
*/
.mentorCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  font: inherit;
  text-align: left;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

.mentorCard:hover {
  border-color: var(--border-strong);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.mentorTop {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

/* Initials when there is no photo — a blank circle is worse than no circle,
   but a named one anchors the card. */
.avatar {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--indigo);
  background: var(--indigo-soft);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mentorIdent {
  min-width: 0;
}

.mentorName {
  display: block;
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text);
}

.mentorTitle {
  display: block;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/*
  The mentor's own words. Clamped to two lines: a long bio must not make one
  card twice the height of its neighbours.
*/
.bio {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rating {
  display: inline-flex;
  align-items: baseline;
  gap: var(--space-1);
  font-size: var(--text-sm);
  color: var(--text);
  white-space: nowrap;
}

.star {
  color: var(--amber);
}

.ratingCount {
  color: var(--text-muted);
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

/* ---------------------------------------------------------- day strip */

/*
  *** GROUPED BY WEEK, NOT ONE WALL OF 21 ***
  Twenty-one identical chips in a flat row give the eye nowhere to land.
  A heading per week, and seven columns, make "next Tuesday" findable
  without reading every label.
*/
/*
  *** THREE WEEKS MUST FIT WITHOUT SCROLLING ***
  The tiles were tall enough that the third row fell off the bottom of a
  1080p window (reported 2026-10-31), which is the same failure the whole
  redesign set out to fix — the student cannot see that "Later" exists.

  So the rows are tight and the labels sit INSIDE the row as an eighth
  column rather than above it, which buys back three lines of height.
*/
.weeks {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.weekLabel {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-subtle);
}

.dayRow {
  display: grid;
  gap: var(--space-2);
  /* The label column, then the seven days. */
  grid-template-columns: 5.5rem repeat(7, minmax(0, 1fr));
  align-items: stretch;
}

.day {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  justify-items: start;
  gap: 0 var(--space-2);
  padding: var(--space-2) var(--space-3);
  font: inherit;
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    background var(--transition-fast);
}

.day:hover:not(:disabled) {
  border-color: var(--indigo);
}

/*
  A day the mentor does not work. Present but inert: removing it entirely
  would shift the columns and break the week's alignment, which is what
  makes the grid readable in the first place.
*/
.day:disabled {
  color: var(--text-subtle);
  background: transparent;
  border-color: transparent;
  cursor: default;
}

/*
  Today, so the first row has an anchor.

  *** NOT THE INDIGO BORDER ***
  That is what `:hover` and `.selected` use, so today read as a second
  selected day sitting beside the real one. A stronger neutral marks it
  without claiming to be a choice.
*/
.dayToday:not(.selected) {
  border-color: var(--border-strong);
  background: var(--bg-sunken);
}

/*
  Two rows inside the tile: "31 Sat" across the top, the count beneath.
  Laid out as a grid so the numbers align down a column however wide the
  weekday abbreviation is in the viewer's locale.
*/
.dayNum {
  grid-column: 1;
  grid-row: 1;
  font-size: var(--text-md);
  font-weight: 600;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.dayName {
  grid-column: 2;
  grid-row: 1;
  font-size: var(--text-xs);
  line-height: 1.2;
  color: var(--text-muted);
}

.dayFree {
  grid-column: 1 / -1;
  grid-row: 2;
  font-size: var(--text-xs);
  line-height: 1.3;
  color: var(--text-subtle);
}

/* On the filled tile the muted greys would vanish, so both inherit white
   and lean on opacity for the hierarchy instead. */
.day.selected .dayName,
.day.selected .dayFree {
  color: inherit;
  opacity: 0.85;
}

/* Today's faint fill must not survive selection. */
.day.dayToday.selected {
  background: var(--indigo);
  border-color: var(--indigo);
}

/* ---------------------------------------------------------- time grid */

/*
  *** GROUPED BY PART OF DAY ***
  Sixteen times in one block is a wall of numbers. Someone booking has a
  rough idea first ("some afternoon") and an exact time second, so the
  headings match how the choice is actually made.
*/
.timeGroups {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/*
  Fixed-width columns rather than flex-wrap: times are all the same shape,
  and a ragged right edge on a list of numbers looks like a mistake.
*/
.times {
  display: grid;
  gap: var(--space-2);
  grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
}

.time {
  padding: var(--space-3) var(--space-2);
  font: inherit;
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    background var(--transition-fast);
}

.time:hover {
  border-color: var(--indigo);
}

/* Session lengths, when a tenant offers more than one. */
.chips {
  display: grid;
  gap: var(--space-3);
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
}

.chip {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4);
  font: inherit;
  text-align: left;
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.chip:hover {
  border-color: var(--border-strong);
  box-shadow: var(--shadow-md);
}

.chipName {
  font-size: var(--text-base);
  font-weight: 600;
}

.chipMins {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/*
  *** ONE SELECTED STATE FOR EVERY CHOICE ***
  Day, time and chip share it. A selection that looked different per control
  would read as three different kinds of thing.

  Filled, not merely outlined: at a glance across a 21-cell grid, a border
  colour alone does not carry. `aria-pressed` carries it for a screen
  reader, so this is not the only signal.
*/
.day.selected,
.time.selected {
  color: #fff;
  background: var(--indigo);
  border-color: var(--indigo);
}

/* The cards stay light — filling a whole card would bury its text. */
.mentorCard.selected,
.chip.selected {
  color: inherit;
  background: var(--bg-elevated);
  border-color: var(--indigo);
  box-shadow: 0 0 0 1px var(--indigo);
}

.chip.selected .chipMins {
  color: var(--indigo);
}

/* --------------------------------------------------------- the summary */

/*
  *** STICKY, SO IT IS THERE WHEN THE DECISION IS MADE ***
  The day step is still taller than a short window. A summary that scrolled
  away would leave Confirm somewhere above the fold at the moment the
  student picks a time.
*/
.summary {
  position: sticky;
  top: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.summaryHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--border);
}

.summaryTitle {
  margin: 0;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-subtle);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

/*
  A button, because clicking a line is the natural way back to the step that
  set it. Unanswered lines are not buttons at all — see the TSX.
*/
.row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: 0;
  font: inherit;
  text-align: left;
  color: inherit;
  background: none;
  border: 0;
  border-radius: var(--radius);
}

.rowBtn {
  cursor: pointer;
}

.rowIcon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  color: var(--text-subtle);
  background: var(--bg-sunken);
  border-radius: var(--radius);
}

.rowSet .rowIcon {
  color: var(--indigo);
  background: var(--indigo-soft);
}

.rowIcon svg {
  width: 1rem;
  height: 1rem;
}

.rowText {
  min-width: 0;
}

.rowLabel {
  display: block;
  font-size: var(--text-xs);
  line-height: 1.3;
  color: var(--text-muted);
}

.rowValue {
  display: block;
  font-size: var(--text-base);
  font-weight: 600;
  line-height: 1.4;
  color: var(--text-subtle);
}

.rowSet .rowValue {
  color: var(--text);
}

/* The "change" affordance, shown on hover of an answered row. */
.rowBtn:hover .rowValue {
  color: var(--indigo);
}

.rowSub {
  display: block;
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-muted);
}

.summaryFoot {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 1px solid var(--border);
}

.priceRow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
}

.priceLabel {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.priceAmount {
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.lineItems {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.lineItems li {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
}

/* "Your plan covers this session" — good news, so it reads as a state and
   not as the muted aside it was. */
.covered {
  align-self: flex-start;
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--success);
  background: var(--success-soft);
  border-radius: var(--radius-full);
}

/* ---------------------------------------------------------------- misc */

.nav {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  margin-top: var(--space-5);
}

/* The button under an empty-state sentence. */
.emptyAction {
  display: flex;
  margin-top: var(--space-4);
}

.muted {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.error {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-sm);
}

/* ------------------------------------------------------------ narrow */

@media (max-width: 64rem) {
  /*
    The rail needs ~20rem to hold a date without wrapping. Below this it
    becomes a band under the steps — still above the fold on a phone,
    because the step panel starts below it.
  */
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .summary {
    position: static;
  }
}

@media (max-width: 60rem) {
  /*
    The label column costs 5.5rem that a narrow window needs for the days,
    so the label goes back above its row. Vertical space is cheap here —
    the page scrolls on a phone regardless.
  */
  .dayRow {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .weekLabel {
    grid-column: 1 / -1;
    padding-top: var(--space-2);
  }
}

@media (max-width: 40rem) {
  /* Four columns, not seven: a seventh of a 320px screen is 45px, which
     cannot hold "12 Wed" and a count. */
  .dayRow {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  /* The connectors are the first thing to go — the numbers already carry
     the sequence, and three pills plus three rules will not fit. */
  .steps {
    flex-wrap: wrap;
  }

  .stepSep {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mentorCard,
  .day,
  .time,
  .chip,
  .stepBtn {
    transition: none;
  }

  .mentorCard:hover {
    transform: none;
  }
}
